import * as Sentry from '@sentry/nextjs'

/** The least a task needs to have a schedule: its id and its stops, in route order. */
export type ScheduledTask = {
  taskDefinition: { id: string; locations: ReadonlyArray<{ plannedArrival: Date | null }> }
}

/** When a task is planned to start and end. */
export type TaskSchedule = { start: Date; end: Date }

export class TaskScheduleError extends Error {
  constructor(taskId: string, reason: string) {
    super(`Task ${taskId} has no usable schedule: ${reason}`)
    this.name = 'TaskScheduleError'
  }
}

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime())

/**
 * A task's scheduled start and end: the planned arrival at its first and last stop.
 *
 * The backend derives the task's fromDatetime/toDatetime from exactly these two values (the first
 * and last of `ordered_locations`, which is also the order the locations arrive in). Reading them
 * from the stops directly lets those attributes be retired from the API with no frontend change.
 *
 * Throws rather than guessing: a missing or unparsed planned arrival is bad data, and quietly
 * substituting a date for it would hide that.
 */
export const getTaskSchedule = (task: ScheduledTask): TaskSchedule => {
  const { id, locations } = task.taskDefinition
  const first = locations[0]
  const last = locations[locations.length - 1]

  if (!first || !last) throw new TaskScheduleError(id, 'it has no stops')
  if (!isValidDate(first.plannedArrival)) {
    throw new TaskScheduleError(id, 'its first stop has no valid planned arrival')
  }
  if (!isValidDate(last.plannedArrival)) {
    throw new TaskScheduleError(id, 'its last stop has no valid planned arrival')
  }

  return { start: first.plannedArrival, end: last.plannedArrival }
}

// Render paths call this on every render, so each bad task is reported once, not once per frame.
const reportedTaskIds = new Set<string>()

/**
 * getTaskSchedule for render paths: one malformed task must not take down the whole board, so this
 * returns null for the caller to show as unknown — and reports the bad data to Sentry, so it is
 * surfaced rather than swallowed.
 */
export const findTaskSchedule = (task: ScheduledTask): TaskSchedule | null => {
  try {
    return getTaskSchedule(task)
  } catch (error) {
    const taskId = task.taskDefinition.id
    if (!reportedTaskIds.has(taskId)) {
      reportedTaskIds.add(taskId)
      Sentry.captureException(error, { tags: { feature: 'tasks' }, extra: { taskId } })
    }
    return null
  }
}
